import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export interface InstructionTemplate {
    id: string;
    template_name: string;
    instruction: string;
    created_at: string;
    updated_at: string;
}

export function useInstructionTemplates(isEnabled: boolean = true) {
    const queryClient = useQueryClient();

    // Fetch templates
    const {
        data: templates = [],
        isLoading: templatesLoading,
        error: templatesError
    } = useQuery<InstructionTemplate[]>({
        queryKey: ['call-instruction-templates'],
        queryFn: async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-instruction-templates`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            
            const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
            
            return body as InstructionTemplate[];
        },
        enabled: isEnabled,
    });

    // Save template mutation
    const saveTemplateMutation = useMutation({
        mutationFn: async ({ templateName, instruction }: { templateName: string; instruction: string }) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-instruction-templates`, {
                method: 'POST',
                headers: { 
                    Authorization: `Bearer ${session.access_token}`, 
                    'Content-Type': 'application/json' 
                },
                body: JSON.stringify({ 
                    template_name: templateName, 
                    instruction: instruction.trim() 
                })
            });
            
            const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
            
            return body as InstructionTemplate;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-instruction-templates'] });
            toast.success('Template saved.');
        },
        onError: (err: any) => { 
            toast.error(`Save failed: ${err.message}`); 
        }
    });

    // Delete template mutation
    const deleteTemplateMutation = useMutation({
        mutationFn: async (templateId: string) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-instruction-templates`, {
                method: 'DELETE',
                headers: { 
                    Authorization: `Bearer ${session.access_token}`, 
                    'Content-Type': 'application/json' 
                },
                body: JSON.stringify({ id: templateId })
            });
            
            const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
            
            return body;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-instruction-templates'] });
            toast.success('Template deleted.');
        },
        onError: (err: any) => { 
            toast.error(`Delete failed: ${err.message}`); 
        }
    });

    return {
        templates,
        templatesLoading,
        templatesError,
        saveTemplate: saveTemplateMutation.mutate,
        deleteTemplate: deleteTemplateMutation.mutate,
        isSaving: saveTemplateMutation.isPending,
        isDeleting: deleteTemplateMutation.isPending,
    };
}
